import config from '../config/config'
import { BgmExtension, BgmTrack } from '../enums/enums'

/** Placeholder storage uses one shared clip per MCQ option letter (A–D), not per answer UUID. */
export const DAX_EXPLANATION_OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const
export type DaxExplanationOptionLetter = typeof DAX_EXPLANATION_OPTION_LETTERS[number]

export function getBgmUrl (trackName: string): string {
  const courseFilesUrl = config.loadEnvironmentVariables().courseFilesUrl
  const baseBgmUrl = `${courseFilesUrl}audio/bgm`
  const extension = getBgmExtension(trackName)

  const url = `${baseBgmUrl}/${trackName}${extension}`

  return url
}

export function getSfxUrl (sfxName: string): string {
  const courseFilesUrl = config.loadEnvironmentVariables().courseFilesUrl
  return `${courseFilesUrl}audio/sfx/${sfxName}.wav`
}

/**
 * Maps a 0-based answer index on an MCQ to option letter A–D.
 * Returns null when the index is outside the four-option placeholder set.
 */
export function getDaxExplanationOptionLetter (answerIndex: number): DaxExplanationOptionLetter | null {
  if (answerIndex < 0 || answerIndex >= DAX_EXPLANATION_OPTION_LETTERS.length) return null
  return DAX_EXPLANATION_OPTION_LETTERS[answerIndex]
}

/**
 * App-asset URL for Dax's shared A–D placeholder explanation VO
 * (`/assets/audio/dax/explanations/{A|B|C|D}.mp3`). Prefer `AnswerData.audioUrl` when the API
 * supplies a per-answer file later. Storage/convention only — not wired to playback.
 */
export function getDaxExplanationAudioUrl (optionLetter: DaxExplanationOptionLetter | string): string {
  return `${process.env.PUBLIC_URL ?? ''}/assets/audio/dax/explanations/${optionLetter}.mp3`
}

/**
 * App-asset URL for Dax's shared A–D placeholder explanation subtitles
 * (`/assets/audio/dax/explanations/{A|B|C|D}.en.vtt`). Prefer `AnswerData.subtitleUrl` when the API
 * supplies a per-answer file later. Storage/convention only — not wired to the speech bubble.
 */
export function getDaxExplanationSubtitleUrl (optionLetter: DaxExplanationOptionLetter | string): string {
  return `${process.env.PUBLIC_URL ?? ''}/assets/audio/dax/explanations/${optionLetter}.en.vtt`
}

/**
 * Builds explanation VO URLs for the selected answers, preserving the question's option order.
 * Index 0→A, 1→B, 2→C, 3→D. Uses `AnswerData.audioUrl` when present.
 * Storage/convention only — not wired to playback.
 */
export function resolveDaxExplanationAudioUrls (
  answersInOrder: Array<{ id: string, audioUrl?: string }>,
  selectedAnswerIds: string[]
): string[] {
  const selected = new Set(selectedAnswerIds)
  const urls: string[] = []

  answersInOrder.forEach((answer, index) => {
    if (!selected.has(answer.id)) return
    if (answer.audioUrl != null) {
      urls.push(answer.audioUrl)
      return
    }
    const letter = getDaxExplanationOptionLetter(index)
    if (letter == null) return
    urls.push(getDaxExplanationAudioUrl(letter))
  })

  return urls
}

/**
 * Builds explanation subtitle URLs for the selected answers, preserving the question's option order.
 * Index 0→A, 1→B, 2→C, 3→D. Uses `AnswerData.subtitleUrl` when present.
 * Storage/convention only — not wired to the speech bubble.
 */
export function resolveDaxExplanationSubtitleUrls (
  answersInOrder: Array<{ id: string, subtitleUrl?: string }>,
  selectedAnswerIds: string[]
): string[] {
  const selected = new Set(selectedAnswerIds)
  const urls: string[] = []

  answersInOrder.forEach((answer, index) => {
    if (!selected.has(answer.id)) return
    if (answer.subtitleUrl != null) {
      urls.push(answer.subtitleUrl)
      return
    }
    const letter = getDaxExplanationOptionLetter(index)
    if (letter == null) return
    urls.push(getDaxExplanationSubtitleUrl(letter))
  })

  return urls
}

export function getBgmExtension (trackName: string): string {
  switch (trackName) {
    case BgmTrack.None: return BgmExtension.None

    case BgmTrack.Dashboard: return BgmExtension.Dashboard
    case BgmTrack.Completed: return BgmExtension.Completed

    case BgmTrack.Cocaine : return BgmExtension.Cocaine
    case BgmTrack.Xanax : return BgmExtension.Xanax
    case BgmTrack.Mdma : return BgmExtension.Mdma
    case BgmTrack.Response : return BgmExtension.Response
    case BgmTrack.Lightning : return BgmExtension.Lightning

    default: return BgmExtension.None
  }
}
